import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fetch current user on mount and refresh
  const fetchUser = async () => {
    try {
      setLoading(true);
      const userData = await apiService.getCurrentUser();
      
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh user on window focus (to catch subscription changes)
  useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated) {
        fetchUser();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Login with email/password
  const login = async (email, password) => {
    try {
      await apiService.login(email, password);
      await fetchUser(); // Refresh user data
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed',
      };
    }
  };

  // Signup with email/password
  const signup = async (email, password, name) => {
    try {
      await apiService.signup(email, password, name);
      await fetchUser(); // Refresh user data
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Signup failed',
      };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await apiService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Logout failed:', error);
      }
    }
  };

  // OAuth login (redirect)
  const loginWithProvider = (provider) => {
    const oauthUrl = apiService.getOAuthUrl(provider);
    window.location.href = oauthUrl;
  };

  // Refresh subscription status (after payment)
  const refreshUser = async () => {
    await fetchUser();
  };

  // Check if user has premium access
  const isPremium = () => {
    if (!user) return false;
    return user.subscriptionStatus === 'active' || user.forceAccess === true;
  };

  // Check if user is on trial
  const isOnTrial = () => {
    if (!user) return false;
    if (!user.trialEndsAt) return false;
    const trialEnd = new Date(user.trialEndsAt);
    return trialEnd > new Date();
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    signup,
    logout,
    loginWithProvider,
    refreshUser,
    isPremium: isPremium(),
    isOnTrial: isOnTrial(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
